import { SDLCMetadata } from "@sscs/types";

export type ReportNavigationState = {
  from: string;
};

export type ReportExportOptions = {
  package_download_enabled: boolean;
  cyclonedx_export_enabled: boolean;
  rl_cve_export_enabled: boolean;
  rl_json_export_enabled: boolean;
  rl_safe_export_enabled: boolean;
  rl_uri_export_enabled: boolean;
  sarif_export_enabled: boolean;
  spdx_export_enabled: boolean;
  rl_summary_pdf_export_enabled: boolean;
};

export type SharedReportOptions = ReportExportOptions & {
  password_protected: boolean;
};

export type SharedReportBaseSettings = {
  label: string;
  expires_in: number;
  expiration_date: string;
} & SharedReportOptions;

export type SharedReportSettings = SharedReportBaseSettings & {
  message?: string;
};

export type SharedTokenBase = {
  token: string;
  settings: SharedReportBaseSettings;
};

export type SharedToken = SharedTokenBase & {
  invalid: boolean;
  user: { first_name: string; last_name: string; email: string } | null;
  created: string;
  password?: string;
  settings: SharedReportSettings;
  recipients: { count: number; viewed_report: number };
};

type FileInfo = {
  file_type: "version" | "filestream";
  product: string;
  package_name: string;
  project_name: string;
  version: string;
  file_name: string;
};

export type SharedReport = {
  latest_report_id: string | null;
  latest_diff_report_id: string | null;
  file_id: string;
  file_info: FileInfo;
  group_id: string;
};

export type SharedReportStats = {
  last_opened: string | null;
  package_download_count: number;
  sbom_export_count: number;
  view_count: number;
  first_shared: string | null;
  number_of_times_shared: number;
  token_count: number;
};

export type LatestReport = SharedReport & {
  shared_reports_stats: SharedReportStats | null;
};

export type LatestReportRequest = {
  groupId: string;
  projectName: string;
  packageName: string;
  version: string;
  build: string | null;
  diffWith?: string;
};

export const SUPPORTED_PURL_IMPORT_OSS_COMMUNITIES = [
  "npm",
  "pypi",
  "rubygems",
  "nuget",
  "vscode",
  "psgallery",
  "maven",
] as const;

// references:
// <internal source control server>/swa-saas/oss-web/blob/develop/src/utils/constants.ts
export const SUPPORTED_OSS_COMMUNITIES = [
  ...SUPPORTED_PURL_IMPORT_OSS_COMMUNITIES,
] as const;

export type Community = (typeof SUPPORTED_OSS_COMMUNITIES)[number];

export type SupportedPurlImportOssCommunity =
  (typeof SUPPORTED_PURL_IMPORT_OSS_COMMUNITIES)[number];

export type PopularityTopLimit = 10 | 100 | 1000 | 10000 | 30000;

export type CommunityAssessmentStatus =
  | SDLCMetadata.assessment_status_t
  | "pending";

export type RepositoryRiskAssessment = Omit<
  SDLCMetadata.risk_assessment_t,
  "status" | "priority"
> & {
  status: CommunityAssessmentStatus;
  priority: null;
};

export type CommunityRiskObject =
  | (SDLCMetadata.risk_object_t & {
      repository?: RepositoryRiskAssessment;
    })
  | { repository: RepositoryRiskAssessment };

export type CommunityArtifact = {
  type: "linked-package" | "rl-safe" | "rl-sbom-cyclonedx" | "rl-sbom-spdx";
  ref: string;
};

export type QuarantineReason =
  | "rl-analyst"
  | "rl-scanner"
  | "rl-heuristics"
  | "ai-analysis"
  | "community";

export type CommunityVersion = {
  version: string;
  published: string;
  quality: {
    status: CommunityAssessmentStatus;
    priority: number | null;
    assessment: string;
    metrics: {
      high: number;
      medium: number;
      low: number;
    };
  };
  artifacts: CommunityArtifact[];
  assessments: CommunityRiskObject;
  incidents: Record<
    string,
    {
      type: "malware" | "removal";
      reporters: {
        type: "community" | "rl-analyst" | "rl-scanner";
        name: string;
        date: string;
        link: string;
      }[];
    }
  >;
};

export type CommunityPackageIdentity = {
  purl: string;
  community: Community;
  namespace: string;
  package: string;
  product: string;
  version: string | null;
  artifact: string | null;
  license: string;
  published: string;
  deprecated: boolean | null;
  removed: boolean;
  category: string | null;
  homepage: string;
  repository: string;
  description: string;
  keywords: string[];
};

export type CommunityPackage = {
  community: {
    package: {
      owner_verified: boolean;
      is_quarantined: boolean | null;
      quarantine: {
        reason: QuarantineReason;
        started_on: string;
        expires_on: null | string;
        expired: boolean;
      };
      first_published: string;
      total_versions: number;
      total_installs: number | null;
      total_downloads: number | null;
      latest_version: string;
      direct_imports: number | null;
      all_malicious: boolean;
      was_archived: boolean | null;
      was_removed: boolean;
      key_project: boolean;
      popularity: PopularityTopLimit | null;
      identity: CommunityPackageIdentity;
      versions: CommunityVersion[];
      incidents: {
        malware: number;
        recent_malware:
          | {
              latest_version: string;
              latest_timestamp: string;
              recent_count: number;
            }
          | Record<string, never>; // empty object allowed, despite docs
        removal: number;
        recent_removal:
          | {
              latest_version: string;
              latest_timestamp: string;
              recent_count: number;
            }
          | Record<string, never>; // empty object allowed, despite docs
      };
      contributors: [
        {
          role: string;
          name: string;
          user: string;
          email: string;
        },
      ];
    };
  };
};

export type CommunityPackageDetails = {
  community: string;
  name: string;
  namespace?: string;
  version?: string;
  artifact?: string;
};

export type CommunityPackageRequest = {
  purl: string;
};
